import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createInvoiceBrandingProfileInputSchema,
  updateInvoiceBrandingProfileInputSchema,
  SUPPORTED_CURRENCIES,
  type InvoiceBrandingProfileOutput,
  type BankProfile,
} from '@1financial/shared';
import { getAccessToken, authenticatedFetch as fetch } from '../../lib/auth-context.js';
import { httpError } from '../../lib/http-error.js';
import { useCapabilities } from '../../hooks/useCapabilities.js';
import { ForbiddenPage } from '../../components/ForbiddenPage.js';

const fields = [
  ['companyName', 'Company display name'], ['addressBlock', 'Address'], ['contactEmail', 'Contact email'],
  ['contactPhone', 'Contact phone'], ['website', 'Website (http or https)'], ['taxRegistration', 'Tax registration'],
  ['accentColor', 'Invoice accent (#RRGGBB)'], ['footerNote', 'Invoice footer'],
] as const;
const bankFields = [
  ['label', 'Label *'], ['bankName', 'Bank name *'], ['accountHolder', 'Account holder *'],
  ['accountNumber', 'Account number'], ['iban', 'IBAN'], ['swiftBic', 'SWIFT / BIC'],
  ['routingCode', 'Routing / sort code'], ['branch', 'Branch'], ['currencyLabel', 'Currency label (optional display)'], ['publicNotes', 'Client-visible payment notes'],
] as const;
const inputClass = 'w-full min-w-0 bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white disabled:opacity-50';
type Profile = InvoiceBrandingProfileOutput;

function newProfile(): Profile {
  return {
    id: 'new', name: 'New brand', active: true, isDefault: false,
    companyName: '1Financial', addressBlock: null, contactEmail: null, contactPhone: null,
    website: null, taxRegistration: null, accentColor: '#10b981', footerNote: null,
    logoUrl: null, bankProfiles: [], updatedAt: new Date().toISOString(),
  };
}

export function InvoiceBrandingPage() {
  const { canDo } = useCapabilities();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [draft, setDraft] = useState<Profile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const canManage = canDo('billing.manage');
  const catalog = useQuery({ queryKey: ['invoice-branding-profiles'], queryFn: async () => {
    const res = await fetch('/v1/invoice-branding/profiles', { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
    if (!res.ok) throw await httpError(res);
    return (await res.json() as { data: { items: Profile[] } }).data.items;
  } });
  useEffect(() => {
    if (!catalog.data) return;
    setProfiles(catalog.data);
    if (!selectedId) setSelectedId(catalog.data.find((p) => p.isDefault)?.id ?? catalog.data[0]?.id ?? 'new');
  }, [catalog.data, selectedId]);
  useEffect(() => {
    if (selectedId === 'new') { setDraft(newProfile()); return; }
    setDraft(profiles.find((p) => p.id === selectedId) ?? null);
    setSaved(false); setError(null);
  }, [selectedId, profiles]);
  const logo = useQuery({ queryKey: ['invoice-branding-profile-logo', draft?.id, draft?.logoUrl], enabled: !!draft?.logoUrl, queryFn: async () => {
    const res = await fetch(draft!.logoUrl!, { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
    if (!res.ok) throw await httpError(res); return URL.createObjectURL(await res.blob());
  } });
  useEffect(() => () => { if (logo.data) URL.revokeObjectURL(logo.data); }, [logo.data]);

  const save = useMutation({ mutationFn: async () => {
    if (!draft) throw new Error('Branding is not loaded');
    const payload = {
      name: draft.name.trim(), active: draft.active, isDefault: draft.isDefault,
      ...Object.fromEntries(fields.map(([key]) => [key, draft[key] || null])), bankProfiles: draft.bankProfiles,
    };
    const creating = draft.id === 'new';
    const parsed = (creating ? createInvoiceBrandingProfileInputSchema : updateInvoiceBrandingProfileInputSchema).safeParse(payload);
    if (!parsed.success) { const e = parsed.error.issues[0]!; throw new Error(`${e.path.join('.')}: ${e.message}`); }
    const res = await fetch(creating ? '/v1/invoice-branding/profiles' : `/v1/invoice-branding/profiles/${draft.id}`, {
      method: creating ? 'POST' : 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` }, body: JSON.stringify(parsed.data),
    });
    if (!res.ok) throw await httpError(res);
    return (await res.json() as { data: Profile }).data;
  }, onSuccess: (data) => {
    setError(null); setSaved(true); setSelectedId(data.id);
    setProfiles((current) => {
      const others = current.filter((p) => p.id !== data.id).map((p) => data.isDefault ? { ...p, isDefault: false } : p);
      return [...others, data].sort((a, b) => Number(b.isDefault) - Number(a.isDefault) || a.name.localeCompare(b.name));
    });
    setDraft(data); void queryClient.invalidateQueries({ queryKey: ['invoice-branding-profiles'] });
  }, onError: (e) => setError(e.message) });

  const upload = useMutation({ mutationFn: async (file: File) => {
    if (!draft || draft.id === 'new') throw new Error('Save the profile before uploading a logo');
    const body = new FormData(); body.append('logo', file);
    const res = await fetch(`/v1/invoice-branding/profiles/${draft.id}/logo`, { method: 'POST', headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` }, body });
    if (!res.ok) throw await httpError(res); return (await res.json() as { data: Profile }).data;
  }, onSuccess: (data) => { setError(null); setDraft(data); setProfiles((current) => current.map((p) => p.id === data.id ? data : p)); }, onError: (e) => setError(e.message) });
  function changeBank(id: string, patch: Partial<BankProfile>) { setSaved(false); setDraft((d) => d ? { ...d, bankProfiles: d.bankProfiles.map((b) => b.id === id ? { ...b, ...patch } : b) } : d); }
  function moveBank(index: number, direction: number) { setDraft((d) => { if (!d) return d; const banks = [...d.bankProfiles]; [banks[index], banks[index + direction]] = [banks[index + direction]!, banks[index]!]; return { ...d, bankProfiles: banks }; }); setSaved(false); }

  if (!canDo('billing.view')) return <ForbiddenPage />;
  return <div className="space-y-4 max-w-4xl min-w-0">
    <div>
      <h2 className="text-base font-semibold text-white">Invoice Branding Profiles</h2>
      <p className="text-sm text-muted">Create independent firm identities and currency-specific payment instructions. Drafts use the selected profile; issued invoices keep their snapshot.</p>
    </div>
    {catalog.error && <p role="alert" className="text-rose-400">{catalog.error.message}</p>}
    {!draft ? <p className="text-sm text-muted">Loading…</p> : <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
      <div className="bg-bg-panel border border-border rounded-xl p-4 grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-3 items-end">
        <label className="text-xs text-muted">Branding profile
          <select value={selectedId} disabled={!canManage} onChange={(e) => setSelectedId(e.target.value)} className={`${inputClass} mt-1`}>
            {profiles.map((p) => <option key={p.id} value={p.id}>{p.name}{p.isDefault ? ' · default' : ''}{!p.active ? ' · inactive' : ''}</option>)}
            {selectedId === 'new' && <option value="new">New profile</option>}
          </select>
        </label>
        <button type="button" disabled={!canManage} onClick={() => setSelectedId('new')} className="border border-border rounded px-3 py-2 text-sm">+ New profile</button>
        <div className="flex gap-4 text-sm text-white pb-2">
          <label><input type="checkbox" checked={draft.active} onChange={(e) => { setSaved(false); setDraft({ ...draft, active: e.target.checked, isDefault: e.target.checked ? draft.isDefault : false }); }} /> Active</label>
          <label><input type="checkbox" checked={draft.isDefault} disabled={!draft.active} onChange={(e) => { setSaved(false); setDraft({ ...draft, isDefault: e.target.checked }); }} /> Default</label>
        </div>
      </div>
      <fieldset disabled={!canManage || save.isPending} className="bg-bg-panel border border-border rounded-xl p-4 space-y-4 min-w-0">
        <label className="block text-xs text-muted">Profile name *
          <input required maxLength={120} value={draft.name} onChange={(e) => { setSaved(false); setDraft({ ...draft, name: e.target.value }); }} className={`${inputClass} mt-1`} />
        </label>
        <div className="flex flex-wrap items-center gap-3">
          {logo.data && <img src={logo.data} alt="Profile logo" className="w-40 h-14 object-contain bg-white rounded" />}
          <input ref={fileInput} type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload.mutate(f); e.target.value = ''; }} />
          <button type="button" disabled={upload.isPending || draft.id === 'new'} onClick={() => fileInput.current?.click()} className="border border-border rounded px-3 py-2 disabled:opacity-50">{upload.isPending ? 'Uploading…' : 'Upload / replace logo'}</button>
          <p className="text-xs text-muted">PNG or JPEG, up to 5 MB. Save a new profile before uploading.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {fields.map(([key, label]) => <label key={key} className="block min-w-0 text-xs text-muted">{label}
            {key === 'addressBlock' || key === 'footerNote' ? <textarea aria-label={label} rows={3} value={draft[key] ?? ''} onChange={(e) => { setSaved(false); setDraft({ ...draft, [key]: e.target.value || null }); }} className={inputClass} /> :
              <input aria-label={label} type="text" value={draft[key] ?? ''} onChange={(e) => { setSaved(false); setDraft({ ...draft, [key]: e.target.value || null }); }} className={inputClass} />}
          </label>)}
        </div>
      </fieldset>
      <fieldset disabled={!canManage || save.isPending} className="bg-bg-panel border border-border rounded-xl p-4 space-y-4 min-w-0">
        <h3 className="font-medium text-white">Bank / payment instructions</h3>
        <p className="text-xs text-muted">Every bank has a real invoice currency. Only active banks exactly matching the invoice currency appear on its PDF and payment page. Multiple matching banks are shown in this order.</p>
        {draft.bankProfiles.map((bank, index) => <div key={bank.id} className="border border-border rounded-lg p-3 space-y-3" data-bank-id={bank.id}>
          <div className="flex flex-wrap gap-2 items-center justify-between">
            <label className="text-sm"><input type="checkbox" checked={bank.active} onChange={(e) => changeBank(bank.id, { active: e.target.checked })} /> Active · Bank {index + 1}</label>
            <div className="flex gap-2">
              <button type="button" aria-label={`Move bank ${index + 1} up`} disabled={index === 0} onClick={() => moveBank(index, -1)}>Up</button>
              <button type="button" aria-label={`Move bank ${index + 1} down`} disabled={index === draft.bankProfiles.length - 1} onClick={() => moveBank(index, 1)}>Down</button>
              <button type="button" aria-label={`Remove bank ${index + 1}`} onClick={() => { setSaved(false); setDraft({ ...draft, bankProfiles: draft.bankProfiles.filter((b) => b.id !== bank.id) }); }} className="text-rose-400">Remove</button>
            </div>
          </div>
          <label className="block text-xs text-muted">Invoice currency *
            <select required value={bank.currency ?? ''} onChange={(e) => changeBank(bank.id, { currency: e.target.value as BankProfile['currency'] })} className={`${inputClass} mt-1`}>
              <option value="">Select currency</option>{SUPPORTED_CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {bankFields.map(([key, label]) => <label key={key} className="text-xs text-muted min-w-0">{label}
              {key === 'publicNotes' ? <textarea aria-label={`Bank ${index + 1} ${label}`} rows={3} value={bank[key]} onChange={(e) => changeBank(bank.id, { [key]: e.target.value })} className={inputClass} /> : <input type="text" aria-label={`Bank ${index + 1} ${label}`} value={bank[key]} onChange={(e) => changeBank(bank.id, { [key]: e.target.value })} className={inputClass} />}
            </label>)}
          </div>
        </div>)}
        <button type="button" disabled={draft.bankProfiles.length >= 10} onClick={() => { setSaved(false); setDraft({ ...draft, bankProfiles: [...draft.bankProfiles, { id: crypto.randomUUID(), label: '', bankName: '', accountHolder: '', accountNumber: '', iban: '', swiftBic: '', routingCode: '', branch: '', currency: null, currencyLabel: '', publicNotes: '', active: true }] }); }} className="border border-border rounded px-3 py-2 disabled:opacity-50">Add bank</button>
      </fieldset>
      {error && <p role="alert" className="text-sm text-rose-400">{error}</p>}
      {canManage && <button type="submit" disabled={save.isPending || upload.isPending} className="bg-accent text-accent-fg rounded px-4 py-2 disabled:opacity-50">{save.isPending ? 'Saving…' : saved ? 'Saved' : 'Save branding profile'}</button>}
    </form>}
  </div>;
}
